import { Table, TableBody, TableCell, TableRow } from '@carbon/react';
import { FORMATTER } from 'lib/ui';
import {
  FreeCashFlowEntry,
  MeasurementType,
  SlotCategory,
} from 'types/evaluation';
import { shownSlotCategoriesIn } from 'utils/slot-categories';
import style from './free-cash-flow-cumulative-table.module.scss';
import { computeCumulativeCashflowSummary } from './cumulative-cashflow-summary';

type Props = {
  freeCashFlows: FreeCashFlowEntry[];
  effectiveDate: string;
  noOfYears: number;
  measurementType: MeasurementType;
  headerLabelContent: Record<SlotCategory | 'total', React.ReactNode>;
};

export const FreeCashFlowCumulativeTable = ({
  freeCashFlows,
  effectiveDate,
  noOfYears,
  headerLabelContent,
  measurementType,
}: Props) => {
  const summary = computeCumulativeCashflowSummary(
    freeCashFlows,
    effectiveDate,
    noOfYears,
    measurementType
  );

  // Check if freeCashFlows is empty
  const isEmpty = freeCashFlows.length === 0;

  // Format function that returns '-' when empty or the formatted value
  const formatValue = (value: number) => {
    return isEmpty ? '-' : `${FORMATTER.format(value)}`;
  };

  // A Create New Well status gets a column only when the run has cash flow in it (INS-13303).
  const columns = [
    ...shownSlotCategoriesIn(
      freeCashFlows,
      (cashflow) => cashflow.category,
      (cashflow) => cashflow[`${measurementType}Cashflow`]
    ),
    'total',
  ] as const;

  return (
    <Table className={style.cumulativeTable}>
      <TableBody>
        <TableRow>
          <TableCell></TableCell>
          {columns.map((column) => (
            <TableCell key={column}>
              <div className={style.cashFlowHeaderCell}>
                {headerLabelContent[column]}
              </div>
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell>
            Cumulative Cash Flow (through {summary.throughYear})
          </TableCell>
          {columns.map((column) => (
            <TableCell key={column} className={style.cashFlowCell}>
              {formatValue(summary.yearLimit[column])}
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell>
            Cumulative Cash Flow (through {summary.finalYear})
          </TableCell>
          {columns.map((column) => (
            <TableCell key={column} className={style.cashFlowCell}>
              {formatValue(summary.limitless[column])}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );
};
